/* eslint-disable array-callback-return */
import React, { useState } from 'react';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import CircularProgress from '@material-ui/core/CircularProgress';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import IconButton from '@material-ui/core/IconButton';
import CancelIcon from '@material-ui/icons/Cancel';
import EditActivitiesDuration from './EditActivitiesDuration';
import BundleVisitActivities from './BundleVisitActivities';
import BundleVisitSlots from './BundleVisitSlots';
import { mapTimeSlotsMatrix } from '../../../actions/areas/shared/mappingHelper';
import { mapBundleVisit } from './mappingHelper';
import { createBundleTreatment } from '../../../actions/areas/planner';
import globals from '../../../globals/communityGlobals';
import useTimeline from '../timeline/useTimeline';
import { treatmentTimeTypes } from '../../../enums/treatmentTimeTypes';
import { trimmedDateAt } from '../../../utils/dates';

const treatmentMsgs = globals.getArea('treatments');
const errorMsgs = globals.getArea('error');

export default function BundleVisitsDetails({ items, onCancel, toggleRefresh }) {
  const { getAccessToken, resetContext } = useTimeline();
  const [showEditDurationPanel, setEditDurationPanel] = useState(false);
  const [overiddenDuration, setOveriddenDuration] = useState(null);
  const [selectedSlotFrom, setSelectedSlotFrom] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [treatmentInstancesSlots, setTreatmentInstancesSlots] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(false);

  const isTimeSlots = items.filter(item => item.treatmentTimeType === treatmentTimeTypes.timeslot);
  const isSpecific = items.filter(item => item.treatmentTimeType === treatmentTimeTypes.specific);
  const isMultiple = items.filter(item => item.treatmentTimeType === treatmentTimeTypes.multiple);

  const canSave = () => {
    if (errorMsg !== '' || isDataLoading) {
      return false;
    }

    if (timeSlots.length > 1 && isSpecific.length >= 1 && isMultiple.length >= 1) {
      return selectedSlotFrom !== null;
    }

    if (timeSlots.length > 1 && isMultiple.length > 1) {
      return selectedSlotFrom !== null;
    }

    if (timeSlots.length > 1 && isSpecific.length > 1) {
      return selectedSlotFrom !== null;
    }

    return true;
  };

  const errorPlaceholder = () => {
    return (
      <span className="form-validation-error">
        <p>{errorMsg}</p>
      </span>
    );
  };

  const resetAndReload = () => {
    setIsDataLoading(false);
    resetContext();
    onCancel();
    toggleRefresh();
  };

  const getSpecificOrMultipleTimeFrom = () => {
    let specificOrMultipleTimeFrom = items.filter(
      x => x.treatmentTimeType === treatmentTimeTypes.specific || x.treatmentTimeType === treatmentTimeTypes.multiple,
    );

    specificOrMultipleTimeFrom = specificOrMultipleTimeFrom.map(x => x.timeSlots[0].from);
    return specificOrMultipleTimeFrom[0];
  };

  const setBundleTreatmentErrorCallback = () => {
    setIsDataLoading(false);
    setErrorMsg(errorMsgs.getMsg('internalError'));
  };

  const saveBundleTreatment = () => {
    setIsDataLoading(true);
    const bundleVisitList = mapBundleVisit(items);

    let specificOrMultipleTimeFrom = null;
    if (selectedSlotFrom === null) {
      specificOrMultipleTimeFrom = getSpecificOrMultipleTimeFrom();
    }

    const bundleTreatmentModel = {};
    bundleTreatmentModel.treatmentInstances = bundleVisitList;
    bundleTreatmentModel.specificTimeFrom = selectedSlotFrom === null ? specificOrMultipleTimeFrom : selectedSlotFrom;
    bundleTreatmentModel.dateOn = trimmedDateAt(items[0].selectedDate);
    bundleTreatmentModel.orgUnitId = items[0].orgUnitId;
    bundleTreatmentModel.overriddenBundleDuration = overiddenDuration;

    if (isTimeSlots.length === items.length) {
      bundleTreatmentModel.timeSlotsMatrix = mapTimeSlotsMatrix(timeSlots);
    }
    createBundleTreatment(getAccessToken, bundleTreatmentModel, resetAndReload, setBundleTreatmentErrorCallback);
  };

  const headerRender = () => {
    const editDurationBtnRender = () => {
      return (
        <List component="nav" style={{ paddingTop: '0px', paddingBottom: '6px' }}>
          <ListItem
            className="header-action-button"
            key={'edit-total-duration-btn'}
            button
            onClick={() => setEditDurationPanel(true)}
          >
            <ListItemIcon>
              <span className="icon-extend icon-edit-mode" />
            </ListItemIcon>
            <ListItemText primary={treatmentMsgs.getMsg('editDuration')} />
          </ListItem>
        </List>
      );
    };

    return (
      <div className="sidePanel-header card-status-green">
        <div className="sidePanel-header-subtitle">
          <Box display="flex">
            <Box flexGrow={1} py={1} />
            <Box>
              <IconButton onClick={() => onCancel()}>
                <CancelIcon />
              </IconButton>
            </Box>
          </Box>
        </div>
        <div className="sidePanel-header-wrapper">
          <div className="sidePanel-header-left-info-wrap">
            <div className="sidePanel-header-title">
              <Typography variant="h2" gutterBottom>
                {treatmentMsgs.getMsg('bundleVisits')}
              </Typography>
            </div>
          </div>
          <div className="sidePanel-header-action">{editDurationBtnRender()}</div>
        </div>
      </div>
    );
  };

  const bundleVisitsFormRender = () => {
    return (
      <React.Fragment>
        <div className="sidePanel-content">
          <div className="clinician-info-box">
            <Typography variant="h6" gutterBottom>
              {treatmentMsgs.getMsg('visitDetails')}
            </Typography>
          </div>
          <BundleVisitActivities items={items} overiddenDuration={overiddenDuration} errorMsg={setErrorMsg} />
          <BundleVisitSlots
            items={items}
            isTimeSlots={isTimeSlots}
            isSpecific={isSpecific}
            isMultiple={isMultiple}
            setSelectedSlotFrom={setSelectedSlotFrom}
            setTreatmentInstancesSlots={setTreatmentInstancesSlots}
            treatmentInstancesSlots={treatmentInstancesSlots}
            setTimeSlots={setTimeSlots}
          />
        </div>
      </React.Fragment>
    );
  };

  const footerRender = () => {
    return (
      <div className="sidePanel-footer">
        <div className="sidePanel-buttons">
          <div className="sidePanel-BackButton">
            <Button variant="contained" color="secondary" onClick={() => onCancel()}>
              {treatmentMsgs.getMsg('cancel')}
            </Button>
          </div>
          <div className="sidePanel-saveButton">
            <Button disabled={!canSave()} variant="contained" color="primary" onClick={saveBundleTreatment}>
              {treatmentMsgs.getMsg('save')}
              {isDataLoading && <CircularProgress style={{ marginLeft: '5px' }} size={18} color="inherit" />}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <section className="side-panel-content-wrapper">
        <SwipeableDrawer anchor="right" open onOpen={() => {}} onClose={() => {}}>
          <div className="sidePanel-wrapper" style={{ width: '650px' }}>
            {headerRender()}
            {errorMsg !== '' && <Box>{errorPlaceholder()}</Box>}
            {bundleVisitsFormRender()}
            {footerRender()}
          </div>
        </SwipeableDrawer>
      </section>
      {showEditDurationPanel && (
        <EditActivitiesDuration
          openDurationPanel={setEditDurationPanel}
          onEditDuration={setOveriddenDuration}
          errorMsg={setErrorMsg}
        />
      )}
    </React.Fragment>
  );
}
